import { useState, useEffect } from 'react';
import { Mail, Loader2, Send } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocationStore } from '@/store/location';

interface EmailReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The order object as loaded by useOrders (with items relation)
  order: any | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailReceiptDialog({ open, onOpenChange, order }: EmailReceiptDialogProps) {
  const { organization } = useAuth();
  const { currency } = useCurrency();
  const { currentLocation } = useLocationStore();

  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Reset the field each time the dialog opens for a different order
  useEffect(() => {
    if (open) setEmail('');
  }, [open, order?.id]);

  const handleSend = async () => {
    const recipient = email.trim();
    if (!EMAIL_REGEX.test(recipient)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!order) return;

    setIsSending(true);
    try {
      // Order items reach us in two shapes depending on the source query:
      // order_items(*) exposes `name`/`total`, while the joined query exposes
      // `product.name`/`total_price`. Tolerate both.
      const items = (order.items || []).map((item: any) => ({
        name: item.name ?? item.product?.name ?? 'Item',
        quantity: item.quantity,
        price: item.unit_price ?? 0,
        total: item.total ?? item.total_price ?? (item.unit_price ?? 0) * (item.quantity ?? 0),
        notes: item.notes ?? undefined,
      }));

      // Surface payment method/card details when the order carries payments.
      const payment = Array.isArray(order.payments) ? order.payments[0] : undefined;

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'order_receipt',
          to: recipient,
          data: {
            order: {
              id: order.id,
              orderNumber: order.order_number,
              items,
              subtotal: order.subtotal ?? 0,
              discount: order.discount_amount ?? 0,
              tax: order.tax_amount ?? 0,
              tip: order.tip_amount ?? 0,
              total: order.total ?? 0,
              createdAt: order.created_at,
              paymentMethod: payment?.payment_method ?? undefined,
              cardLast4: payment?.card_last_four ?? undefined,
            },
            organizationName: organization?.name ?? 'BlazePOS',
            locationName: currentLocation?.name ?? undefined,
            currency,
          },
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('Receipt sent', {
        description: `Emailed to ${recipient}`,
      });
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Failed to send receipt', {
        description: err.message || 'Please try again.',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent>
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Receipt
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            {order ? `Send the receipt for order #${order.order_number}.` : 'Send the receipt.'}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>
        <ResponsiveModalBody className="space-y-2">
          <Label htmlFor="receipt-email">Recipient email</Label>
          <Input
            id="receipt-email"
            type="email"
            inputMode="email"
            placeholder="customer@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isSending) handleSend();
            }}
            autoFocus
          />
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isSending} className="gap-2">
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send receipt
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
